import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const DEFAULT_DDI = "+55";

const SignUpPage = () => {
  const { signUp, isAuthenticated, loading, error } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fullPhone = useMemo(() => {
    const cleaned = localPhone.trim();
    return cleaned ? `${DEFAULT_DDI} ${cleaned}` : DEFAULT_DDI;
  }, [localPhone]);

  useEffect(() => {
    if (!isAuthenticated) return;

    navigate("/", { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setLocalError(null);

    try {
      const result = await signUp?.(email, password, {
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        phone: fullPhone,
      });

      if (result) {
        const message =
          "Conta criada! Agora confirme seu e-mail para ativar o acesso. Enviamos um link de confirmação para o seu e-mail. Verifique também o spam/lixo eletrônico.";
        setSuccessMessage(message);
        toast({ title: "Conta criada", description: message });
      }
    } catch (signUpError) {
      const message =
        (signUpError as Error)?.message ||
        error ||
        "Não foi possível criar sua conta. Tente novamente.";
      setLocalError(message);
      toast({ title: "Erro ao criar conta", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando sessão...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-6 space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Criar sua conta</h1>
          <p className="text-sm text-muted-foreground">Crie seu acesso para usar o Ferroz Tracking</p>
        </div>

        <Card className="border border-border/70 shadow-elegant">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl">Portal de Cadastro</CardTitle>
            <CardDescription>Preencha os dados para liberar seu painel</CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <div className="mb-5 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
                <p className="font-semibold text-primary">
                  Conta criada! Agora confirme seu e-mail para ativar o acesso.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Enviamos um link de confirmação para o seu e-mail. Verifique também o spam/lixo eletrônico.
                </p>
              </div>
            )}
            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Nome</Label>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    placeholder="Seu nome"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={submitting || !!successMessage}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Sobrenome</Label>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    placeholder="Seu sobrenome"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={submitting || !!successMessage}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Seu e-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="voce@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting || !!successMessage}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <div className="grid gap-3 md:grid-cols-[140px,1fr]">
                  <div className="relative">
                    <Input id="ddi" value={DEFAULT_DDI} readOnly className="font-medium" />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                      BR
                    </span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel-national"
                    placeholder="(11) 99999-9999"
                    value={localPhone}
                    onChange={(e) => setLocalPhone(e.target.value)}
                    required
                    disabled={submitting || !!successMessage}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Crie uma senha segura"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    required
                    disabled={submitting || !!successMessage}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    disabled={submitting || !!successMessage}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {(localError || error) && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {localError || error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting || !!successMessage}>
                {submitting ? "Enviando..." : "Continuar"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="bg-muted/50 justify-center text-sm text-muted-foreground">
            <span className="mr-2">Já possui uma conta?</span>
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Entrar
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default SignUpPage;

